import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowUp, CalendarDays, Eye, Map, MessageCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import UpgradePrompt from "./UpgradePrompt";
import PlanBadge from "./PlanBadge";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useOperatorApi } from "@/hooks/useRoleBasedApi";

interface OperatorDashboardProps {
  isPremium?: boolean;
  isBasic?: boolean;
  operatorName?: string;
}

interface DashboardData {
  profileViews: number;
  profileViewsChange: number;
  contactClicks: number;
  contactClicksChange: number;
  bookingRequests: number;
  bookingRequestsChange: number;
  upcomingBookings: number;
  profileCompletion: number;
  engagementData: { date: string; interactions: number }[];
}

const OperatorDashboard: React.FC<OperatorDashboardProps> = ({ 
  isPremium = false, 
  isBasic = false,
  operatorName = "Your Business" 
}) => {
  const operatorApi = useOperatorApi();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const dashboardData = await operatorApi.getDashboardData();
        setData(dashboardData);
      } catch (err) {
        setError("Failed to load dashboard data. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const userPlan = isPremium ? 'premium' : isBasic ? 'basic' : 'free';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sauna-orange"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-l-4 border-red-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle />
            Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card className="border-l-4 border-l-sauna-blue overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-sauna-navy to-sauna-navy/95 text-white py-5 px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-xl font-semibold">{operatorName} Dashboard</CardTitle>
              <p className="text-sm font-normal text-gray-300">Your listing is already generating interest</p>
            </div>
            <div>
              <PlanBadge requiredPlan={userPlan} userPlan={userPlan} showFullText={true} />
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {/* Engagement Trends Chart - Positioned at top */}
          <div className="px-6 pt-6">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle>Engagement Trends</CardTitle>
                  {userPlan === 'free' ? (
                    <PlanBadge 
                      requiredPlan="premium" 
                      userPlan={userPlan} 
                      tooltip="Upgrade to Premium to view engagement trends" 
                    />
                  ) : (
                    <PlanBadge 
                      requiredPlan={userPlan} 
                      userPlan={userPlan} 
                      tooltip={"Premium analytics with detailed insights"} 
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  {userPlan !== 'free' ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.engagementData}>
                        <defs>
                          <linearGradient id="colorInteractions" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F97316" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#F97316" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis 
                          dataKey="date" 
                          fontSize={12} 
                          tickMargin={10}
                          stroke="#888"
                        />
                        <YAxis 
                          fontSize={12}
                          tickMargin={10}
                          stroke="#888"
                        />
                        <Tooltip />
                        <Area 
                          type="monotone" 
                          dataKey="interactions" 
                          stroke="#F97316" 
                          fillOpacity={1}
                          fill="url(#colorInteractions)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center bg-gray-50 rounded-md border border-dashed border-gray-300">
                      <p className="text-gray-500 font-medium mb-2">Premium Feature</p>
                      <p className="text-gray-500 text-sm mb-4">Upgrade to Premium to visualize customer engagement trends over time.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Profile Views & Clicks - Moved up below Engagement Trends */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            {userPlan !== 'free' ? (
              <>
                <Card className="shadow-sm border border-gray-100 transition-all hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center text-sauna-blue">
                        <Eye size={18} className="mr-2" strokeWidth={2.5} />
                        <h3 className="text-sm font-medium uppercase tracking-wide">Profile Views</h3>
                      </div>
                      <PlanBadge requiredPlan="premium" userPlan={userPlan} />
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-sauna-navy">{data.profileViews}</span>
                      <span className="text-sauna-green text-sm flex items-center font-medium">
                        <ArrowUp size={16} className="inline mr-0.5" /> {data.profileViewsChange}% this week
                      </span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="shadow-sm border border-gray-100 transition-all hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center text-sauna-orange">
                        <Map size={18} className="mr-2" strokeWidth={2.5} />
                        <h3 className="text-sm font-medium uppercase tracking-wide">Contact Clicks</h3>
                      </div>
                      <PlanBadge requiredPlan="premium" userPlan={userPlan} />
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-sauna-navy">{data.contactClicks}</span>
                      <span className="text-sauna-green text-sm flex items-center font-medium">
                        <ArrowUp size={16} className="inline mr-0.5" /> {data.contactClicksChange}% this week
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <UpgradePrompt 
                feature="Detailed Engagement Stats"
                description="See how many customers are viewing your profile and clicking on your contact information."
              />
            )}
          </div>
          
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 pt-0">
            <Card className="shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center text-sauna-green mb-3">
                  <CalendarDays size={18} className="mr-2" strokeWidth={2.5} />
                  <h3 className="text-sm font-medium uppercase tracking-wide">Booking Requests</h3>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-sauna-navy">{data.bookingRequests}</span>
                  <span className="text-sauna-green text-sm flex items-center font-medium">
                    <ArrowUp size={16} className="inline mr-0.5" /> {data.bookingRequestsChange}% this month
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center text-purple-600 mb-3">
                  <MessageCircle size={18} className="mr-2" strokeWidth={2.5} />
                  <h3 className="text-sm font-medium uppercase tracking-wide">Upcoming Bookings</h3>
                </div>
                <span className="text-3xl font-bold text-sauna-navy">{data.upcomingBookings}</span>
              </CardContent>
            </Card>

            <Card className="shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <CardContent className="p-5">
                <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-2">Profile Completion</h3>
                <Progress value={data.profileCompletion} className="w-full h-3" />
                <div className="flex justify-between mt-2">
                  <p className="text-sm font-medium">{data.profileCompletion}% complete</p>
                  <Link to="/operator/profile" className="text-sm text-sauna-blue hover:underline">Improve now</Link>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Actions */}
          <div className="px-6 pb-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button asChild variant="outline" className="h-20 flex-col gap-1">
                <Link to="/operator/profile">
                  <Eye className="w-6 h-6" />
                  <span>View Profile</span>
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-20 flex-col gap-1">
                <Link to="/operator/bookings">
                  <CalendarDays className="w-6 h-6" />
                  <span>Manage Bookings</span>
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-20 flex-col gap-1">
                <Link to="/operator/services">
                  <Map className="w-6 h-6" />
                  <span>Edit Services</span>
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-20 flex-col gap-1">
                <Link to="/operator/messages">
                  <MessageCircle className="w-6 h-6" />
                  <span>View Messages</span>
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {!isPremium && <UpgradePrompt />}
    </div>
  );
};

export default OperatorDashboard;
